import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { colors } from '../styles/colors';
import { globalStyles } from '../styles/globalStyles';
import CustomButton from '../components/CustomButton';
import { useUser } from '../context/UserContext';

// Responsive helper functions
const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useUser();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await login(formData.email, formData.password);

      if (response.success) {
        const isAdminUser = response.data.is_admin === true ||
          response.data.role === 'Administrator' ||
          response.data.role === 'Super Admin' ||
          response.data.role === 'Placement Officer' ||
          response.data.role === 'Head Office' ||
          response.data.role === 'Hall Manager';

        setTimeout(() => {
          navigate(isAdminUser ? '/admin' : '/home', { state: { userData: response.data } });
        }, 100);
      } else {
        alert(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>Welcome Back</div>
          <div style={styles.subtitle}>Sign in to your account to continue booking</div>
        </div>

        {/* Login Form */}
        <div style={styles.formContainer}>
          <div style={styles.formCard}>
            <div style={styles.cardTitle}>User Login</div>

            {/* Email Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                style={styles.input}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your email address"
                autoCapitalize="none"
                autoComplete="email"
              />
            </div>

            {/* Password Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  style={styles.passwordInput}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  style={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span style={styles.passwordToggleText}>
                    {showPassword ? '🙈' : '👁️'}
                  </span>
                </button>
              </div>
            </div>

            {/* Login Button */}
            <CustomButton
              title={loading ? 'Signing In...' : 'Sign In'}
              onPress={handleLogin}
              disabled={loading || !formData.email || !formData.password}
              style={styles.loginButton}
            />

            {/* Divider */}
            <div style={styles.divider}>
              <div style={styles.dividerLine}></div>
              <span style={styles.dividerText}>OR</span>
              <div style={styles.dividerLine}></div>
            </div>

            {/* Signup Link */}
            <div style={styles.signupContainer}>
              <span style={styles.signupText}>Don't have an account? </span>
              <Link to="/signup" style={styles.signupLink}>
                Create Account
              </Link>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  header: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(24, 32, 40),
    textAlign: 'center',
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  subtitle: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.white,
    opacity: 0.9,
  },
  formContainer: {
    padding: getResponsiveValue(20, 32, 40),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  formCard: {
    ...globalStyles.card,
    width: '100%',
    maxWidth: getResponsiveValue(400, 450, 500),
    padding: getResponsiveValue(24, 32, 40),
  },
  cardTitle: {
    fontSize: getResponsiveValue(20, 24, 28),
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: getResponsiveValue(24, 32, 40),
  },
  inputGroup: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  label: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    display: 'block',
  },
  input: {
    width: '100%',
    padding: getResponsiveValue(12, 16, 20),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    width: '100%',
    padding: getResponsiveValue(12, 16, 20),
    paddingRight: getResponsiveValue(40, 48, 56),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordToggle: {
    position: 'absolute',
    right: getResponsiveValue(8, 12, 16),
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: getResponsiveValue(4, 6, 8),
  },
  passwordToggleText: {
    fontSize: getResponsiveValue(16, 18, 20),
  },
  loginButton: {
    width: '100%',
    marginTop: getResponsiveValue(8, 12, 16),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    marginTop: getResponsiveValue(16, 20, 24),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    marginLeft: getResponsiveValue(12, 16, 20),
    marginRight: getResponsiveValue(12, 16, 20),
  },
  signupContainer: {
    textAlign: 'center',
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  signupText: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.textLight,
  },
  signupLink: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: '600',
  },
};

export default LoginPage;
